// hooks/useDomainMutation.ts
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  MutationFunction,
} from "@tanstack/react-query";
import { useReadonlyContext } from "../context/read-only.context";

function createReadonlyError(message: string) {
  return {
    response: {
      data: {
        message,
      },
    },
  };
}

/**
 * Exact mirror of TanStack v4 useMutation with domain guard
 */
export function useDomainMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: MutationFunction<TData, TVariables>,
  options?: UseMutationOptions<TData, TError, TVariables, TContext> & {
    allowWhenReadonly?: boolean;
  }
): UseMutationResult<TData, TError, TVariables, TContext>;

export function useDomainMutation<
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext> & {
    allowWhenReadonly?: boolean;
  }
): UseMutationResult<TData, TError, TVariables, TContext>;

export function useDomainMutation<TData, TError, TVariables, TContext>(
  arg1:
    | MutationFunction<TData, TVariables>
    | (UseMutationOptions<TData, TError, TVariables, TContext> & {
        allowWhenReadonly?: boolean;
      }),
  arg2?: UseMutationOptions<TData, TError, TVariables, TContext> & {
    allowWhenReadonly?: boolean;
  }
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { readonly } = useReadonlyContext();

  // ✅ Normalize args
  const mutationFn = typeof arg1 === "function" ? arg1 : arg1.mutationFn;

  const options = typeof arg1 === "function" ? arg2 ?? {} : arg1;

  console.log(readonly, options?.allowWhenReadonly);
  return useMutation({
    ...options,
    mutationFn: async (variables: TVariables) => {
      if (readonly && !options?.allowWhenReadonly) {
        throw createReadonlyError(
          "This action is not allowed in the current readonly state"
        );
      }

      if (!mutationFn) {
        throw createReadonlyError("mutationFn is required");
      }

      return mutationFn(variables);
    },
  });
}
